import {
  Add as AddIcon,
  FilterAltOutlined as FilterIcon,
} from '@mui/icons-material';
import { Stack, Typography } from '@mui/material';
import { Button, theme } from '@sb/cc-design-system';
import { get, isEmpty } from 'lodash';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BulkActionButton } from 'src/components/bulkActionButton/BulkActionButton';
import { CanaryAcknowledgeModalContainer } from 'src/components/canaryDeploymentNotice';
import { FilterModal } from 'src/components/filterModal';
import { SearchBar } from 'src/components/input';
import LayoutPanel from 'src/components/layoutPanel';
import { OverflowFilterChips } from 'src/components/overflowFilterChips';
import { PublishActionButton } from 'src/components/publishActionButton/PublishActionButton';
import { OO_LOCATIONS_PAGE_SIZE } from 'src/constants';
import { PATHS } from 'src/constants/paths';
import { useModal } from 'src/hooks';
import { useGetLocationOwnerTags } from 'src/hooks/api/locationOwnerTags/useGetLocationOwnerTags';
import { useSearchFilter } from 'src/hooks/useSearchFilter';
import { ILocationListing } from 'src/interfaces';
import { lang } from 'src/utils';

import { LocationListTable } from './components/LocationListTable';
import { LocationOwnerTags } from './components/LocationOwnerTags';
import { getSearchKeyToUse } from './helper/searchKeyUtils';
import { useLocationFilterPills } from './hooks/useLocationFilterPills';
import { useLocationFilters } from './hooks/useLocationFilters';
import { useLocationTab } from './hooks/useLocationTab.hook';
import {
  FilterBadge,
  FilterHeaderStack,
  RootContainer,
  SearchToolbarActions,
  SearchToolbarStack,
} from './LocationListPageOOV2.styled';

export const LocationListPageOOV2: React.FC = () => {
  const {
    locations,
    isLocationTabDataLoading,
    totalCount,
    totalPages,
    activeFilters,
    handlePageChange,
    onSortChangeHandle,
    selectedSortOption,
    page,
    rowsPerPage,
    onRowsPerPageChange,
    locationsContainOutdoorType,
    searchParams,
    getParamsValue,
  } = useLocationTab({ defaultPageSize: OO_LOCATIONS_PAGE_SIZE });
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState<string>('');
  const { handleOnChangeFilterKeyword } = useSearchFilter();
  const filterModal = useModal();

  const handleEditCODMessaging = () => {
    navigate({
      pathname: PATHS.LOCATIONS_BULK_EDIT_MESSAGING,
    });
  };

  const getBannerChecked = (row: ILocationListing) => {
    const config = row.config;

    if (!config || (typeof config === 'object' && isEmpty(config))) {
      return true;
    }

    const bannerActive = get(
      config,
      'cod.confirmSetting.header.review.banner.active',
    );

    return typeof bannerActive === 'boolean' ? bannerActive : true;
  };

  const getMessagingChecked = (row: ILocationListing) => {
    const config = row.config;

    if (!config || (typeof config === 'object' && isEmpty(config))) {
      return true;
    }

    const hideCandidates: Array<boolean | undefined> = [
      get(config, 'cod.codSetting.topMessage.hideRmhcMessage'),
      get(config, 'cod.confirmSetting.topMessage.hideRmhcMessage'),
    ];

    const hideValues = hideCandidates.filter(
      (value): value is boolean => typeof value === 'boolean',
    );

    if (hideValues.length === 0) {
      return true;
    }

    return hideValues.every((hideValue) => !hideValue);
  };

  const handleClearFilters = () => {
    onResetFilters();
    setSearchValue('');
  };

  const handleAddSearchValue = () => {
    if (searchValue.trim()) {
      const keyToUse = getSearchKeyToUse(activeFilters);
      const currentValues = getParamsValue(keyToUse);

      // Check if the search value already exists to ensure uniqueness
      if (!currentValues.includes(searchValue)) {
        handleOnChangeFilterKeyword([...currentValues, searchValue], keyToUse);
        setSearchValue('');
      }
    }
  };

  const getSearchFilterPills = () => {
    const keyToUse = getSearchKeyToUse(activeFilters);
    const values = getParamsValue(keyToUse);

    return Array.isArray(values) ? values : [];
  };

  const {
    isFuzzySearch,
    setIsFuzzySearch,
    onChangeToExactMatch,
    onChangeToFuzzyMatch,
    onChangeKeyword,
    onRemoveKeyword,
    onChangeFilter,
    onResetFilters,
  } = useLocationFilters(activeFilters);

  const locationOwnerTagIds = activeFilters['location_owner_tag_id'] || [];
  const { data: locationOwnerTagsData } = useGetLocationOwnerTags(
    [`id.list=${locationOwnerTagIds.join('|')}`, 'pageSize=0'],
    { enabled: locationOwnerTagIds.length > 0 },
  );

  const locationOwnerTagNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    locationOwnerTagsData?.pages
      ?.flatMap((page) => page.data)
      .forEach((tag) => {
        map[String(tag.id)] = tag.name;
      });

    return map;
  }, [locationOwnerTagsData]);

  const filterPills = useLocationFilterPills({
    activeFilters,
    locationOwnerTagNameMap,
    onChangeFilter,
  });

  return (
    <RootContainer>
      <CanaryAcknowledgeModalContainer />
      <LayoutPanel data-testid="layout-panel">
        <LayoutPanel.Header data-testid="layout-header" showBottomBorder>
          <FilterHeaderStack>
            <BulkActionButton
              onEditCODMessaging={handleEditCODMessaging}
              dropdownStyles={{ marginRight: 1 }}
            />
            <PublishActionButton
              label={lang.publish}
              variant="outlined-secondary-1"
              size="sm"
              startIcon={<AddIcon />}
              locationCodes={locations.map(
                (location) => location.location_code,
              )}
            />
          </FilterHeaderStack>
        </LayoutPanel.Header>
        <LayoutPanel.Header
          showBorder={false}
          isSecondHeader
          showBottomBorder={false}>
          <SearchToolbarStack direction="row" paddingY="12px">
            <SearchBar
              placeholder={lang.search + '...'}
              value={searchValue}
              onInputChange={(value) => setSearchValue(String(value))}
              onKeyDown={(e) => e.key === 'Enter' && handleAddSearchValue()}
            />
            <SearchToolbarActions direction="row" gap={1}>
              <Button
                variant="text"
                startIcon={<FilterIcon />}
                endIcon={
                  Object.keys(activeFilters).length > 0 && (
                    <FilterBadge>
                      <Typography>
                        {Object.keys(activeFilters).length}
                      </Typography>
                    </FilterBadge>
                  )
                }
                onClick={filterModal.onOpenModal}>
                {lang.filters}
              </Button>

              {(getSearchFilterPills().length > 0 ||
                Object.keys(activeFilters).length > 0) && (
                <Button
                  variant="text"
                  onClick={(e) => {
                    e.preventDefault();
                    handleClearFilters();
                  }}>
                  {lang.clearFilter}
                </Button>
              )}
            </SearchToolbarActions>

            {(getSearchFilterPills().length > 0 ||
              Object.keys(activeFilters).length > 0) && (
              <OverflowFilterChips items={filterPills} />
            )}
          </SearchToolbarStack>
        </LayoutPanel.Header>
        <LayoutPanel.ContentContainer data-testid="layout-content-container">
          <Stack sx={{ flex: 1, overflow: 'auto' }}>
            <LocationListTable
              rows={locations}
              itemsToShowCheckbox={locationsContainOutdoorType}
              loading={isLocationTabDataLoading}
              selectedSortOption={selectedSortOption}
              onSortChange={onSortChangeHandle}
              page={page}
              totalPages={totalPages}
              totalCount={totalCount}
              onPageChange={handlePageChange}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={onRowsPerPageChange}
              getBannerChecked={getBannerChecked}
              getMessagingChecked={getMessagingChecked}
              bodyBackgroundColor={theme.palette.common.white}
              onRowClick={(row: ILocationListing) =>
                navigate({
                  pathname: PATHS.LOCATION_DETAILS(
                    row.location_code.toString(),
                  ),
                  search: searchParams,
                })
              }
              renderTags={(row: ILocationListing) => (
                <LocationOwnerTags tags={row.location_owner_tags} />
              )}
            />
          </Stack>
        </LayoutPanel.ContentContainer>
      </LayoutPanel>
      <FilterModal
        open={filterModal.open}
        onClose={filterModal.onCloseModal}
        onApply={filterModal.onCloseModal}
        onChangeFilter={onChangeFilter}
        onChangeKeyword={onChangeKeyword}
        onRemoveKeyword={onRemoveKeyword}
        isFuzzySearch={isFuzzySearch}
        setIsFuzzySearch={setIsFuzzySearch}
        onChangeToExactMatch={onChangeToExactMatch}
        onChangeToFuzzyMatch={onChangeToFuzzyMatch}
        hasExactSearch
        activeFilters={activeFilters}
        getParamsValue={getParamsValue}
      />
    </RootContainer>
  );
};
